// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0

/**
 * Converts a duration in minutes to a human-readable value/unit pair.
 * Thresholds:
 *   <= 120 min  -> minutes
 *   <= 2880 min -> hours   (48h)
 *   <= 10080 min-> days    (7d)
 *   <= 40320 min-> weeks   (4w)
 *   <= 201600 min-> months (48 months... wait, see note)
 *   above       -> years
 *
 * @param {number} minutes
 * @returns {{ value: number, unit: 'minute'|'hour'|'day'|'week'|'month'|'year' }}
 */
export function minutesToValueAndUnit(minutes) {
  const THRESHOLDS = [
    { limit: 2 * 60, divisor: 1, unit: 'minute' },
    { limit: 48 * 60, divisor: 60, unit: 'hour' },
    { limit: 7 * 24 * 60, divisor: 60 * 24, unit: 'day' },
    { limit: 4 * 7 * 24 * 60, divisor: 60 * 24 * 7, unit: 'week' },
    { limit: 48 * 30 * 24 * 60, divisor: 60 * 24 * 30, unit: 'month' },
  ];

  for (const { limit, divisor, unit } of THRESHOLDS) {
    if (minutes <= limit) {
      return { value: Math.round(minutes / divisor), unit };
    }
  }

  return { value: Math.round(minutes / (60 * 24 * 365)), unit: 'year' };
}

/**
 * Maps a unit key and value to the correct translated UI string (singular/plural).
 *
 * Expected uiTexts structure:
 * {
 *   units: {
 *     minute: { singular: 'Minute', plural: 'Minuten' },
 *     hour:   { singular: 'Stunde', plural: 'Stunden' },
 *     day:    { singular: 'Tag',    plural: 'Tage'    },
 *     week:   { singular: 'Woche',  plural: 'Wochen'  },
 *     month:  { singular: 'Monat',  plural: 'Monate'  },
 *     year:   { singular: 'Jahr',   plural: 'Jahre'   },
 *   }
 * }
 *
 * @param {'minute'|'hour'|'day'|'week'|'month'|'year'} unit
 * @param {number} value
 * @param {object} uiTexts
 *  The uiTexts loaded from ui Store
 * @returns {string}
 */
export function getTranslatedUnit(unit, value, uiTexts) {
  const unitTexts = uiTexts?.units?.[unit];
  if (!unitTexts) return unit; // Fallback: raw unit-key

  return value === 1 ? unitTexts.singular : unitTexts.plural;
}
